import { FieldConfig, FieldDataTypes, NameableInterface, nString, uNumber, Uuid } from '@hidat/huijs-common';
import { SplixerResourceInterface } from './splixer-resource.interface';
import { ResourceConfig, SearchFilterConfig } from '@hidat/huijs-resource';
import { splixerFieldConfigs, splixerSystemFields } from './splixer-fields.const';
import { SortOrder } from '@hidat/huijs-api';
import { ZonedDateTime } from '@js-joda/core';
import { BrandedResource, ExtendedBrandingFields, ExtendedBrandingInterface } from './branded-resource.interface';
import * as dayjs from 'dayjs';
import { MixFilterKlasses, MixMediaTypes, MixMicBreakModes, MixPlaybackTypes } from './splixer-types';
import { HashViews } from './hash-views';
import { MixConfigInterface } from './mix-filter.interface';

export const MixConfig: ResourceConfig = new ResourceConfig('Mix', 'mixes')
  .addFields(
    [
      splixerFieldConfigs.uid,
      splixerFieldConfigs.status,

      splixerFieldConfigs.name.setRequired(true),
      splixerFieldConfigs.tagLine,
      splixerFieldConfigs.description,
      splixerFieldConfigs.imageUrl,
      splixerFieldConfigs.branding,

      new FieldConfig('categoryCode'),
      new FieldConfig('mediaType'),
      new FieldConfig('sourceType'),
      new FieldConfig('sourceId', FieldDataTypes.NUMBER),
      new FieldConfig('duration', FieldDataTypes.INTEGER).setEditable(false),
      new FieldConfig('minDuration', FieldDataTypes.NUMBER),
      new FieldConfig('featured', FieldDataTypes.BOOLEAN),
      new FieldConfig('isSteerable', FieldDataTypes.BOOLEAN),
      new FieldConfig('isSelectable', FieldDataTypes.BOOLEAN),
      new FieldConfig('hasPlaylist', FieldDataTypes.BOOLEAN),
      new FieldConfig('displayShow', FieldDataTypes.BOOLEAN),
      new FieldConfig('displayHost', FieldDataTypes.BOOLEAN),
      new FieldConfig('playbackType'),
      new FieldConfig('schedulingKlass'),
      new FieldConfig('steerableTagLine'),
      new FieldConfig('priority', FieldDataTypes.INTEGER),
      new FieldConfig('urlStub'),
      new FieldConfig('broadcastedAt', FieldDataTypes.DATETIME),
      new FieldConfig('hostName'),

      splixerFieldConfigs.programId,
      splixerFieldConfigs.channelId,
      splixerFieldConfigs.hostId,
    ],
    true,
    HashViews.player,
  )
  .addFields([
    splixerFieldConfigs.intId,
    splixerFieldConfigs.stationId,
    splixerFieldConfigs.remoteId,
    splixerFieldConfigs.remoteCode,
    new FieldConfig('shortCode'),
    new FieldConfig('mixMode'),
    new FieldConfig('filterKlass'),
    new FieldConfig('filterConfig', FieldDataTypes.HASH).setEditable(false),
    new FieldConfig('startsAt', FieldDataTypes.DATETIME),
    new FieldConfig('endsAt', FieldDataTypes.DATETIME),
    new FieldConfig('steerableStartsAt', FieldDataTypes.DATETIME),
    new FieldConfig('steerableEndsAt', FieldDataTypes.DATETIME),
    new FieldConfig('validSegments', FieldDataTypes.INTEGER).setEditable(false),

    ...splixerSystemFields,
  ])
  .setSorts({
    name: {
      dbName: 'name',
      defaultOrder: SortOrder.ASC,
      secondarySort: {
        dbName: 'tagLine',
        order: SortOrder.DESC,
      },
    },
    tagLine: 'tagLine',
    categorycode: {
      dbName: 'categoryCode',
      secondarySort: {
        dbName: 'name',
      },
    },
    mediaType: {
      dbName: 'mediaType',
      secondarySort: {
        dbName: 'name',
      },
    },
    status: 'status',
    startsat: {
      dbName: 'startsAt',
      defaultOrder: SortOrder.DESC,
    },
    endsat: {
      dbName: 'endsAt',
      defaultOrder: SortOrder.DESC,
    },
    broadcastedat: {
      dbName: 'broadcastedAt',
      defaultOrder: SortOrder.DESC,
    },
    createdat: {
      dbName: 'createdAt',
      defaultOrder: SortOrder.DESC,
    },
  })
  .setDefaultSort('name');

export const MixIncludes = {
  source: 'source',
  program: 'program',
  host: 'host',
};

export const MixSearchFilterConfig = new SearchFilterConfig(MixConfig, {
  only: [
    'name',
    'tagline',
    'shortCode',
    'categoryCode',
    'mediaType',
    'programId',
    'hostId',
    'featured',
    'status',
  ],
})
  .addQ()
  .addField('showId', FieldDataTypes.UID)
  .addRangeField('startsAt', FieldDataTypes.DATE)
  .addRangeField('broadcastedAt', FieldDataTypes.DATE)
  .addField('available', FieldDataTypes.BOOLEAN);

export const MixBrandingFields = [...ExtendedBrandingFields];

export interface MixBrandingInterface extends ExtendedBrandingInterface {}

/** Mix Filter used by the Players **/
export type MixSearchTypes = 'all' | 'recent' | 'featured' | 'startsWith';
export interface PlayerMixFilter {
  q?: string; // Full Text Query
  startsWithRange?: string;
  klass?: string;
  category?: string;
  tag?: string;
  maxItems?: number;
  broadcastedOn?: string; //ISO formated broadcast date
  broadcastedDays?: number; // Return all mixes broadcasted in the last x days
}

/**
 * Subset of fields used by the PlayerMix
 */
export interface PlayerMixInterface
  extends
    SplixerResourceInterface<number>,
    NameableInterface,
    BrandedResource<number> {
  uid: Uuid | undefined;

  name: string | undefined;
  description: string | undefined;
  tagLine: string | undefined;
  steerableTagLine: nString;
  imageUrl: string | undefined;
  branding: MixBrandingInterface | undefined;
  priority: uNumber;
  urlStub: nString;

  featured: boolean | undefined;
  hasPlaylist: boolean | undefined;
  displayShow: boolean | undefined;
  displayHost: boolean | undefined;

  shortCode: string | undefined;
  categoryCode: string | undefined;
  mediaType: MixMediaTypes | undefined;
  filterConfig: MixConfigInterface | undefined;
  filterKlass: MixFilterKlasses | undefined;
  micBreakMode: MixMicBreakModes | undefined;
  minDurationHours: number | undefined;
  playbackType: MixPlaybackTypes | undefined;
  schedulingKlass: string | undefined;
  validSegments: number | undefined;
  duration: number | undefined;
  stationId: number | undefined;
  programId: number | undefined;
  sourceType: string | undefined;
  //sourceId: number | undefined;
  hostId: number | undefined;
  hostName: string | undefined;
  broadcastedAt: dayjs.Dayjs | ZonedDateTime | undefined | null;
  isSteerable: boolean | undefined;
  isSelectable: boolean | undefined;
}

/**
 * Full Content Path
 * As used by the administrator
 */
export interface MixInterface extends PlayerMixInterface {
  tenantId: Uuid | undefined;
  channelId: number | undefined;
  startsAt: ZonedDateTime | undefined | null;
  endsAt: ZonedDateTime | undefined | null;
  steerableStartsAt: ZonedDateTime | undefined | null;
  steerableEndsAt: ZonedDateTime | undefined | null;
}

export interface MixSearchFilter {
  minPriority?: number;
  featured?: boolean;
  categoryCode?: string;
}
